import {
  Text,
  Button,
  useModalState,
  Modal,
  InputSelectControlField,
  TextInputField,
  Checkbox,
  Label,
  Primary13Regular,
  Primary13SemiBold,
  Banner,
  RedRed60,
  IconPlus,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useState, useMemo, useCallback } from 'react'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { AnrokTaxCategory } from 'SettingsV2/components/tax-rates/taxRates.types'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'

interface Props {
  taxCategoryId: string
  taxCategoryName: string
  onAddMapping: (values: {
    taxCategoryId: string
    taxCategoryName: string
    anrokTaxCategoryId: string
    anrokTaxCategoryType: 'standard' | 'custom'
    isDefault: boolean
  }) => Promise<void>
}

const AddAnrokMappingModalUI = ({
  taxCategoryId,
  taxCategoryName,
  onAddMapping,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      anrokTaxCategory: '',
      isDefault: false
    },
    fieldConfiguration: [
      {
        property: 'anrokTaxCategory',
        validation: [required]
      },
      {
        property: 'isDefault',
        validation: []
      }
    ]
  })

  const {
    data: anrokTaxCategoriesData,
    isLoading: isLoadingCategories,
    isError: hasError
  } = useQuery(dashboardv99990101Client.getAnrokTaxCategories)

  const anrokTaxCategories = anrokTaxCategoriesData?.items || []

  const comboBoxOptions = useMemo(
    () =>
      anrokTaxCategories.map((category: AnrokTaxCategory) => ({
        value: JSON.stringify({
          type: category.type,
          id: category.id
        }),
        label: category.name
      })),
    [anrokTaxCategories]
  )

  const [selectedValue, setSelectedValue] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const canSubmit =
    queries.isValid && selectedValue && !isSubmitting && !isLoadingCategories

  const handleChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      fields.anrokTaxCategory.onChange(value)
    },
    [fields.anrokTaxCategory]
  )

  const handleSubmit = useCallback(async () => {
    if (selectedValue && !isSubmitting && queries.isValid) {
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        const categoryId = JSON.parse(selectedValue) as {
          type: 'standard' | 'custom'
          id: string
        }
        await onAddMapping({
          taxCategoryId,
          taxCategoryName,
          anrokTaxCategoryId: categoryId.id,
          anrokTaxCategoryType: categoryId.type,
          isDefault: fields.isDefault.value
        })
        onClose()
      } catch (error) {
        console.error('Failed to add Anrok mapping:', error)
        setSubmitError(
          getApiErrorMessage(error, 'Failed to link Anrok tax code to category')
        )
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [
    selectedValue,
    isSubmitting,
    queries.isValid,
    taxCategoryId,
    taxCategoryName,
    fields.isDefault.value,
    onAddMapping,
    onClose
  ])

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Add Anrok mapping
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3}>
          {submitError && (
            <Banner variant="error" data-testid="addAnrokMapping.submitError">
              {submitError}
            </Banner>
          )}
          <Flex flexDirection="column" gap={2}>
            <Text {...Primary13Regular}>Mapping for:</Text>
            <Text as="span" {...Primary13SemiBold}>
              {taxCategoryName}
            </Text>
          </Flex>

          {hasError && (
            <Text
              color={RedRed60}
              {...Primary13Regular}
              data-testid="addAnrokMapping.error"
            >
              Failed to load Anrok tax categories. Please try again.
            </Text>
          )}

          <InputSelectControlField
            label="Anrok tax category"
            placeholder={
              isLoadingCategories
                ? 'Loading categories...'
                : 'Select a tax category...'
            }
            options={comboBoxOptions}
            initialValue={selectedValue}
            onChange={handleChange}
            disabled={isLoadingCategories}
            data-testid="anrokTaxCategoryComboBox"
          />

          <Checkbox
            isChecked={fields.isDefault.value}
            onChange={e => fields.isDefault.onChange(e.target.checked)}
            label="Mark as default"
            data-testid="addAnrokMapping.isDefault"
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          disabled={isSubmitting}
          data-testid="addAnrokMapping.cancel"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="addAnrokMapping.submit"
        >
          {isSubmitting ? 'Adding...' : 'Add mapping'}
        </Button>
      </Modal.Footer>
    </>
  )
}

const AddAnrokMappingModal = ({ ...props }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        variant="ghost"
        onClick={openModal}
        leadingIcon={<IconPlus color="inherit" />}
        data-testid="addAnrokMapping.trigger"
      >
        Add Anrok mapping
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="anrokMappingModal"
      >
        <AddAnrokMappingModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default AddAnrokMappingModal
